import React from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { FacilityEntity, FacilityEntityType } from '@/types/facility';
import { ENTITY_RENDER_CONFIG } from '@/types/facility';
import {
  ArrowUpFromLine, Columns3, Scissors, Crosshair, ScanLine,
  Target, Droplets, Square, Car, LayoutGrid, ParkingSquare,
} from 'lucide-react';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  ArrowUpFromLine, Columns3, Scissors, Crosshair, ScanLine,
  Target, Droplets, Square, Car, LayoutGrid,
};

interface ToolPaletteProps {
  onAddEntity: (type: FacilityEntityType) => void;
  onAddParkingRow: () => void;
}

const WORK_BAY_TYPES: FacilityEntityType[] = [
  'lift_2post', 'lift_4post', 'lift_scissor',
  'alignment', 'adas', 'alignment_adas',
  'wash', 'flat',
];

export function ToolPalette({ onAddEntity, onAddParkingRow }: ToolPaletteProps) {
  return (
    <div data-testid="facility-tool-palette" className="bg-card flex flex-col shrink-0 max-h-[50%] overflow-hidden">
      <div className="p-3 border-b border-border">
        <h2 className="text-sm font-semibold">Tools</h2>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-4">
          {/* Work Bays */}
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Work Bays</p>
            <div className="space-y-1">
              {WORK_BAY_TYPES.map(type => {
                const cfg = ENTITY_RENDER_CONFIG[type];
                const Icon = ICON_MAP[cfg.icon] ?? Square;
                return (
                  <Button
                    key={type}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 h-8 text-xs"
                    onClick={() => onAddEntity(type)}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {cfg.displayLabel}
                  </Button>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Parking */}
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Parking</p>
            <div className="space-y-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2 h-8 text-xs"
                onClick={onAddParkingRow}
              >
                <ParkingSquare className="h-3.5 w-3.5" />
                Add Parking Row
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2 h-8 text-xs"
                onClick={() => onAddEntity('parking_space')}
              >
                <Car className="h-3.5 w-3.5" />
                Single Space
              </Button>
            </div>
          </div>

          <Separator />

          {/* Zones (future) */}
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Zones</p>
            <div className="space-y-1">
              <Button variant="ghost" size="sm" className="w-full justify-start gap-2 h-8 text-xs" disabled>
                <LayoutGrid className="h-3.5 w-3.5" />
                Work Area
              </Button>
              <Button variant="ghost" size="sm" className="w-full justify-start gap-2 h-8 text-xs" disabled>
                <Square className="h-3.5 w-3.5" />
                Storage
              </Button>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
